// Downloads signed URLs and blobs, not the API.
// eslint-disable-next-line no-restricted-imports
import axios, { AxiosResponse } from 'axios';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { ensureExtension } from './model';
import {
  FILE_DOWNLOAD_ERROR,
  FileDownloadSource,
  TransientBlobUrl,
} from './types';

export class BlobLifecycleService {
  createPersistentBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  createTransientBlobUrl(blob: Blob): TransientBlobUrl {
    const url = this.createPersistentBlobUrl(blob);

    return {
      url,
      cleanup: () => this.revokeBlobUrl(url),
    };
  }

  revokeBlobUrl(url: string): void {
    if (!isBlobUrl(url)) {
      return;
    }

    URL.revokeObjectURL(url);
  }
}

export const blobLifecycleService = new BlobLifecycleService();

export const downloadBlob = async (request: string): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await axios(request, {
    responseType: 'blob',
  });

  return new Blob([response.data]);
};

export const saveBlobOnUsersDevice = async (
  blob: Blob,
  fileName: string
): Promise<void> => {
  const transientBlob = blobLifecycleService.createTransientBlobUrl(blob);
  const link = document.createElement('a');

  try {
    link.href = transientBlob.url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    transientBlob.cleanup();
  }
};

export const downloadExternalFile = async (
  src: string,
  filename: string
): Promise<void> => {
  if (!src || !filename) {
    throw new Error(FILE_DOWNLOAD_ERROR);
  }

  const response = await fetch(src);
  const blob = await response.blob();

  await saveBlobOnUsersDevice(blob, filename);
};

export const downloadFile = async (
  src: FileDownloadSource,
  filename: string | undefined,
  extension: string | undefined
): Promise<void> => {
  if (!src || !filename) {
    throw new Error(FILE_DOWNLOAD_ERROR);
  }

  const ensuredName = ensureExtension(filename, extension);

  if (typeof src === 'string') {
    await downloadExternalFile(src, ensuredName);
    return;
  }

  await saveBlobOnUsersDevice(src, ensuredName);
};

export {
  canDocumentBeDownloadedAndIsImage,
  ensureExtension,
} from './model';

export { FILE_DOWNLOAD_ERROR } from './types';

export type {
  FileDownloadSource,
  TransientBlobUrl,
} from './types';
